import { OWNER_EDITABLE_DPIA_STATUSES } from 'config/api/dpias/dpias.constants';
import type { DpiaDetail, DpiaRisk, DpiaStatus, UpdateDpiaInput } from 'config/api/dpias/dpias.types';

export type OwnerDpiaFormValues = {
  plannedProcessingDescription: string;
  assessmentScope: string;
  projectOrChangeContext: string;
  selectedPurposeIds: number[];
  necessityAssessment: string;
  proportionalityAssessment: string;
  dataMinimisationAssessment: string;
  retentionAndAccessAssessment: string;
  overallAssessment: string;
  residualRiskLevel: UpdateDpiaInput['residualRiskLevel'];
  priorConsultationAssessment: UpdateDpiaInput['priorConsultationAssessment'];
  ownerConclusion: string;
  ownerResponseComment: string;
  risks: DpiaRisk[];
};

export type OwnerDpiaValidationMessages = {
  required: string;
  purposeScope: string;
  riskTitle: string;
  riskDescription: string;
  riskAffectedRights: string;
  riskLikelihood: string;
  riskImpact: string;
  riskMeasures: string;
  measureTitle: string;
  measureDescription: string;
  measureResponsible: string;
  measureStatus: string;
  clarificationResponse: string;
  residualRisk: string;
  priorConsultation: string;
};

export type CompletenessItem = {
  id: string;
  complete: boolean;
};

const isBlank = (value: string) => !value.trim();

export const isOwnerDpiaEditable = (status: DpiaStatus) =>
  OWNER_EDITABLE_DPIA_STATUSES.includes(status as typeof OWNER_EDITABLE_DPIA_STATUSES[number]);

export const mapDetailToFormValues = (detail: DpiaDetail): OwnerDpiaFormValues => ({
  plannedProcessingDescription: detail.plannedProcessingDescription,
  assessmentScope: detail.assessmentScope,
  projectOrChangeContext: detail.projectOrChangeContext,
  selectedPurposeIds: detail.selectedPurposeIds,
  necessityAssessment: detail.necessityAssessment,
  proportionalityAssessment: detail.proportionalityAssessment,
  dataMinimisationAssessment: detail.dataMinimisationAssessment,
  retentionAndAccessAssessment: detail.retentionAndAccessAssessment,
  overallAssessment: detail.overallAssessment,
  residualRiskLevel: detail.residualRiskLevel,
  priorConsultationAssessment: detail.priorConsultationAssessment,
  ownerConclusion: detail.ownerConclusion,
  ownerResponseComment: detail.ownerResponseComment,
  risks: detail.risks,
});

export const mapFormValuesToUpdateInput = (detail: DpiaDetail, values: OwnerDpiaFormValues): UpdateDpiaInput => ({
  revision: detail.revision,
  plannedProcessingDescription: values.plannedProcessingDescription,
  assessmentScope: values.assessmentScope,
  projectOrChangeContext: values.projectOrChangeContext,
  selectedPurposeIds: values.selectedPurposeIds,
  necessityAssessment: values.necessityAssessment,
  proportionalityAssessment: values.proportionalityAssessment,
  dataMinimisationAssessment: values.dataMinimisationAssessment,
  retentionAndAccessAssessment: values.retentionAndAccessAssessment,
  overallAssessment: values.overallAssessment,
  residualRiskLevel: values.residualRiskLevel,
  priorConsultationAssessment: values.priorConsultationAssessment,
  ownerConclusion: values.ownerConclusion,
  ownerResponseComment: values.ownerResponseComment,
});

export const buildCompletenessItems = (values: OwnerDpiaFormValues, status: DpiaStatus): CompletenessItem[] => {
  const hasRiskWithMeasure = values.risks.some(risk => risk.measures.length > 0);

  return [
    { id: 'planned_processing', complete: !isBlank(values.plannedProcessingDescription) },
    {
      id: 'necessity_proportionality',
      complete:
        !isBlank(values.necessityAssessment) &&
        !isBlank(values.proportionalityAssessment) &&
        !isBlank(values.dataMinimisationAssessment) &&
        !isBlank(values.retentionAndAccessAssessment),
    },
    { id: 'at_least_one_risk', complete: values.risks.length > 0 },
    {
      id: 'risk_measures',
      complete: values.risks.length > 0 && values.risks.every(risk => risk.measures.length > 0) && hasRiskWithMeasure,
    },
    { id: 'residual_risk', complete: values.residualRiskLevel !== 'not_assessed' },
    { id: 'prior_consultation', complete: values.priorConsultationAssessment !== 'not_assessed' },
    {
      id: 'clarification_response',
      complete: status !== 'clarification_requested' || !isBlank(values.ownerResponseComment),
    },
  ];
};

export const validateDpiaForSubmit = (
  values: OwnerDpiaFormValues,
  status: DpiaStatus,
  messages: OwnerDpiaValidationMessages,
): Partial<Record<string, string>> => {
  const errors: Partial<Record<string, string>> = {};

  if (isBlank(values.plannedProcessingDescription)) errors.plannedProcessingDescription = messages.required;
  if (isBlank(values.assessmentScope)) errors.assessmentScope = messages.required;
  if (isBlank(values.projectOrChangeContext)) errors.projectOrChangeContext = messages.required;
  if (values.selectedPurposeIds.length === 0) errors.selectedPurposeIds = messages.purposeScope;
  if (isBlank(values.necessityAssessment)) errors.necessityAssessment = messages.required;
  if (isBlank(values.proportionalityAssessment)) errors.proportionalityAssessment = messages.required;
  if (isBlank(values.dataMinimisationAssessment)) errors.dataMinimisationAssessment = messages.required;
  if (isBlank(values.retentionAndAccessAssessment)) errors.retentionAndAccessAssessment = messages.required;
  if (isBlank(values.overallAssessment)) errors.overallAssessment = messages.required;
  if (values.residualRiskLevel === 'not_assessed') errors.residualRiskLevel = messages.residualRisk;
  if (values.priorConsultationAssessment === 'not_assessed') errors.priorConsultationAssessment = messages.priorConsultation;
  if (isBlank(values.ownerConclusion)) errors.ownerConclusion = messages.required;
  if (status === 'clarification_requested' && isBlank(values.ownerResponseComment)) {
    errors.ownerResponseComment = messages.clarificationResponse;
  }

  if (values.risks.length === 0) {
    errors.risks = messages.riskTitle;
  }

  values.risks.forEach((risk, riskIndex) => {
    if (isBlank(risk.title)) errors[`risk.${riskIndex}.title`] = messages.riskTitle;
    if (isBlank(risk.description)) errors[`risk.${riskIndex}.description`] = messages.riskDescription;
    if (isBlank(risk.affectedRightsAndFreedoms)) errors[`risk.${riskIndex}.affectedRightsAndFreedoms`] = messages.riskAffectedRights;
    if (!risk.likelihood) errors[`risk.${riskIndex}.likelihood`] = messages.riskLikelihood;
    if (!risk.impact) errors[`risk.${riskIndex}.impact`] = messages.riskImpact;
    if (risk.measures.length === 0) errors[`risk.${riskIndex}.measures`] = messages.riskMeasures;

    risk.measures.forEach((measure, measureIndex) => {
      if (isBlank(measure.title)) errors[`risk.${riskIndex}.measure.${measureIndex}.title`] = messages.measureTitle;
      if (isBlank(measure.description)) errors[`risk.${riskIndex}.measure.${measureIndex}.description`] = messages.measureDescription;
      if (!measure.responsibleMembershipId) errors[`risk.${riskIndex}.measure.${measureIndex}.responsible`] = messages.measureResponsible;
      if (!measure.status) errors[`risk.${riskIndex}.measure.${measureIndex}.status`] = messages.measureStatus;
    });
  });

  return errors;
};

export const hasValidationErrors = (errors: Partial<Record<string, string>>) => Object.keys(errors).length > 0;

export const createEmptyRisk = (nextId: number, responsibleMembershipId: number, responsibleName: string): DpiaRisk => ({
  id: nextId,
  title: '',
  description: '',
  affectedRightsAndFreedoms: '',
  likelihood: '',
  impact: '',
  riskLevel: '',
  sortOrder: nextId > 0 ? nextId : 9999,
  measures: [
    {
      id: nextId < 0 ? nextId - 1 : nextId * 100,
      dpiaRiskId: nextId,
      title: '',
      description: '',
      responsibleMembershipId,
      responsibleName,
      dueDate: null,
      status: 'planned',
      sortOrder: 1,
    },
  ],
});

export const createEmptyMeasure = (nextId: number, riskId: number, responsibleMembershipId: number, responsibleName: string) => ({
  id: nextId,
  dpiaRiskId: riskId,
  title: '',
  description: '',
  responsibleMembershipId,
  responsibleName,
  dueDate: null,
  status: 'planned' as const,
  sortOrder: nextId > 0 ? nextId : 9999,
});
